import React, { useEffect, useRef, useState } from 'react'
import DsButton from './Button'
import Eyebrow from './Eyebrow'
import { ChevronDownIcon, CheckIcon } from './icons'
import { SettingsSaveState } from './SettingsSaveContext'

export interface SettingsFooterTab {
  id: string,
  label: string,
  count?: number,
  danger?: boolean,
}

/**
 * Everything that lives below the content on a Settings screen.
 *
 * Two strips, stacked, above the mobile tab bar: the save bar on top — it acts
 * on the content, so it sits nearer to it — and the section picker below,
 * grouped with the navigation it belongs to. On desktop the picker is gone (the
 * tab row is on screen) and the bar returns to the bottom of the window.
 *
 * The picker opens upward because it is anchored at the bottom, which is where
 * a thumb reaches. It answers a question the horizontal tab strip could not:
 * the active pill there scrolls out of view, so the strip says where you can go
 * without ever saying where you are.
 */
const SettingsFooter = ({ saveState, tabs, activeTabId, onSelectTab }: {
  saveState: SettingsSaveState | null,
  tabs: SettingsFooterTab[],
  activeTabId: string,
  /** The caller decides whether the move is allowed — it is what knows there
   * are unsaved changes to warn about. */
  onSelectTab: (id: string) => void,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = tabs.find(tab => tab.id === activeTabId);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const saveBar = saveState && saveState.dirtyCount > 0 ? (
    <div className="h-[62px] px-4 lg:px-8 bg-navy-800 border-t border-navy-500 flex items-center justify-between gap-3">
      <span className="min-w-0 truncate text-ds-135 text-navy-200">
        {saveState.dirtyCount} unsaved change{saveState.dirtyCount === 1 ? '' : 's'} in this section
      </span>
      <div className="flex items-center gap-2 flex-none">
        <DsButton variant="outline" onClick={saveState.onDiscard} isDisabled={saveState.isSubmitting}>
          Discard
        </DsButton>
        <DsButton
          variant="primary"
          onClick={saveState.onSave}
          isDisabled={saveState.isSubmitting || !saveState.canSave}
        >
          {saveState.isSubmitting ? 'Saving…' : 'Save changes'}
        </DsButton>
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={ref}
      className="fixed z-30 left-0 right-0 lg:left-[232px] bottom-[calc(75px_+_env(safe-area-inset-bottom))] lg:bottom-0 flex flex-col"
    >
      {/* The panel, above both strips. It is a list rather than a native
          <select> so the counts and the danger tone survive. */}
      {isOpen ? (
        <div className="lg:hidden mx-3 mb-2 rounded-container border border-navy-500 bg-navy-800 shadow-[0_8px_32px_rgba(0,0,0,.5)] overflow-hidden">
          <div className="px-4 pt-3.5 pb-2"><Eyebrow>settings</Eyebrow></div>
          {tabs.map(tab => {
            const isActive = tab.id === activeTabId;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => { setIsOpen(false); onSelectTab(tab.id); }}
                className={[
                  'b4-focus w-full min-h-[48px] px-4 flex items-center gap-3 text-left text-ds-135',
                  'border-t border-navy-700 transition-colors duration-[120ms]',
                  isActive ? 'bg-navy-600 text-white font-semibold' : tab.danger ? 'text-state-err' : 'text-navy-100',
                ].join(' ')}
              >
                <span className="flex-1 min-w-0 truncate">{tab.label}</span>
                {tab.count ? (
                  <span className="flex-none font-courrier text-ds-115 text-navy-300">{tab.count}</span>
                ) : null}
                {isActive ? <CheckIcon size={16} className="flex-none" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {saveBar}

      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(prev => !prev)}
        className="b4-focus lg:hidden h-[56px] px-4 bg-navy-800 border-t border-navy-500 flex items-center justify-between gap-3 text-left"
      >
        <span className="flex flex-col gap-0.5 min-w-0">
          <Eyebrow>section</Eyebrow>
          <span className={`text-ds-135 font-semibold truncate ${active?.danger ? 'text-state-err' : 'text-white'}`}>
            {active?.label}
          </span>
        </span>
        <ChevronDownIcon
          size={16}
          className={`flex-none text-navy-300 transition-transform duration-[120ms] ${isOpen ? 'rotate-0' : 'rotate-180'}`}
        />
      </button>
    </div>
  );
}

export default SettingsFooter
